import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  SwipeableDrawer,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { CurrencyFixedDecimalInput } from '@/shared/formatters';
import { CoverageType, Renter } from '../../utils/types/renter.interface';
interface CoverageSelectionProps {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  selfEnrolledSDRLimit?: number;
}

const texts = {
  rentCoverage: {
    title: 'Rent Coverage',
    subtitle: 'Months of coverage',
    description:
      'Example: If the tenant has a 12 month lease with 1 month free, and the 1st month paid at lease signing, enter “10” for full coverage.',
  },
  securityDepositCoverage: {
    title: 'Security Deposit Coverage',
    subtitle: 'Months of coverage',
    description:
      'Example: If you want to collect the equivalent of 1 month security deposit, enter $2,500 for a monthly rent of $2,500.',
  },
} as const;

const CoverageSelection = ({ control, errors, selfEnrolledSDRLimit }: CoverageSelectionProps) => {
  const theme = useTheme();
  const [infoOpen, setInfoOpen] = useState(false);
  const [type, setType] = useState<CoverageType>();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const handleInfoClick = () => {
    setInfoOpen((prev) => !prev);
  };

  const onTooltipClick = (selectedType: CoverageType) => {
    setType(selectedType);
    setInfoOpen(true);
  };

  return (
    <>
      <Typography mb={1} variant="h5">
        Customize your coverage selection
      </Typography>
      <Typography mb={4}>
        You may select exactly how many months of Rent Coverage and Deposit Coverage you'd like.
      </Typography>
      <Typography variant="h6" mb={1}>
        Rent Coverage
      </Typography>
      <Box mb={4}>
        <Controller
          control={control}
          name="monthsOfCoverage"
          defaultValue={undefined}
          render={({ field }) => (
            <TextField
              label="Months of coverage"
              id="months-of-coverage"
              type="number"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {!isDesktop ? (
                      <IconButton onClick={() => onTooltipClick(CoverageType.RentCoverage)}>
                        <InfoOutlinedIcon sx={{ color: 'grey' }} />
                      </IconButton>
                    ) : (
                      <Tooltip
                        title={`Example: If the tenant has a 12 month lease with 1 month free, and the 1st month paid at lease signing, enter “10” for full coverage.`}
                        placement="right"
                        componentsProps={{ tooltip: { sx: { width: '220px' } } }}
                        enterTouchDelay={0}
                      >
                        <IconButton>
                          <InfoOutlinedIcon sx={{ color: 'grey' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </InputAdornment>
                ),
              }}
              {...field}
              error={!!errors.monthsOfCoverage}
              helperText={
                (errors.monthsOfCoverage && errors.monthsOfCoverage?.message?.toString()) ?? ''
              }
              required
            />
          )}
        />
        <Stack direction="column" px={1.75} justifyContent={'space-between'}>
          <Typography variant="helpertext">
            Please enter the number of months you’d like covered excluding prepaid rent, concessions
            and security deposit.
          </Typography>
        </Stack>
      </Box>
      <Box mb={4}>
        <Typography variant="h6" mb={1}>
          Security Deposit Coverage
        </Typography>
        <Box>
          <Controller
            control={control}
            name="securityDepositReplacementAmount"
            render={({ field }) => (
              <TextField
                {...field}
                ref={null}
                id="security-deposit-replacement-amount"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      {!isDesktop ? (
                        <IconButton
                          onClick={() => onTooltipClick(CoverageType.SecurityDepositCoverage)}
                        >
                          <InfoOutlinedIcon sx={{ color: 'grey' }} />
                        </IconButton>
                      ) : (
                        <Tooltip
                          title={`Example: If you want to collect the equivalent of 1 month security deposit, enter $2,500 for a monthly rent of $2,500.`}
                          placement="right"
                          componentsProps={{ tooltip: { sx: { width: '220px' } } }}
                          enterTouchDelay={0}
                        >
                          <IconButton>
                            <InfoOutlinedIcon sx={{ color: 'grey' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                  inputComponent: CurrencyFixedDecimalInput,
                  disableUnderline: true,
                }}
                label="Security deposit replacement amount"
                error={!!errors.securityDepositReplacementAmount}
                helperText={
                  (errors.securityDepositReplacementAmount &&
                    errors.securityDepositReplacementAmount?.message?.toString()) ??
                  ''
                }
                required
              />
            )}
          />
          <Stack px={1.75} direction="column" justifyContent={'space-between'}>
            <Typography variant="helpertext">
              If you are allowing your renter to replace a cash deposit, please enter the dollar
              amount.
              {!selfEnrolledSDRLimit
                ? ''
                : ` Value cannot exceed the equivalent of ${selfEnrolledSDRLimit} month's rent.`}
            </Typography>
          </Stack>
        </Box>
      </Box>
      {isTablet && (
        <Dialog
          fullWidth={true}
          maxWidth="md"
          onClose={handleInfoClick}
          open={infoOpen}
          PaperProps={{
            style: { borderRadius: 20 },
          }}
        >
          <DialogTitle sx={{ m: 0, pt: 4, pb: 1, px: 4 }}>
            <Box sx={{ fontSize: '22px' }}>{type && texts[type].title}</Box>
            <IconButton
              aria-label="close"
              onClick={handleInfoClick}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 5 }}>
            <Typography variant="subheader">{type && texts[type].subtitle}</Typography>
            <Typography variant="subheader">
              <Box mt={2}>{type && texts[type].description}</Box>
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 5, pb: 5 }}>
            <Button autoFocus onClick={handleInfoClick} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {isMobile && (
        <SwipeableDrawer
          PaperProps={{ square: false }}
          sx={{
            '& .MuiDrawer-paper': {
              borderRadius: '30px 30px 0 0',
            },
          }}
          open={infoOpen}
          anchor="bottom"
          onClose={handleInfoClick}
          onOpen={handleInfoClick}
        >
          <Box mx={5} mt={5}>
            <Typography mb={1} variant="h4">
              {type && texts[type].title}
            </Typography>
            <Typography mb={2} variant="subheader">
              {type && texts[type].subtitle}
            </Typography>

            <Box
              sx={{
                lineHeight: '26px',
                mt: 2,
              }}
            >
              <Typography variant="subheader">{type && texts[type].description}</Typography>
            </Box>
            <Box mt={4} mb={3}>
              <Button variant="contained" onClick={handleInfoClick} fullWidth>
                Close
              </Button>
            </Box>
          </Box>
        </SwipeableDrawer>
      )}
    </>
  );
};

export default CoverageSelection;
